import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { FileText, Image, Search, User, Tag, AlertCircle, Check } from 'lucide-react';
import {
  BulkUploadDocumentView,
  EmployeeSimpleView,
  CompanyAttachmentTypeView,
  getEmployeesForMapping,
  getAttachmentTypes,
} from '../../../../api/General/bulkUpload';

interface MapDocumentsStepProps {
  documents: BulkUploadDocumentView[];
  mappings: Record<number, { userId: string; companyAttachmentTypeId: number; expiryDate?: string; reminderDays?: number }>;
  onMappingsChange: (mappings: Record<number, { userId: string; companyAttachmentTypeId: number; expiryDate?: string; reminderDays?: number }>) => void;
}

export default function MapDocumentsStep({
  documents,
  mappings,
  onMappingsChange,
}: MapDocumentsStepProps) {
  const { t } = useTranslation('common');
  const [employees, setEmployees] = useState<EmployeeSimpleView[]>([]);
  const [attachmentTypes, setAttachmentTypes] = useState<CompanyAttachmentTypeView[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [empResult, typeResult] = await Promise.all([
          getEmployeesForMapping(),
          getAttachmentTypes(),
        ]);
        if (empResult.errors?.length === 0) setEmployees(empResult.payload);
        if (typeResult.errors?.length === 0) setAttachmentTypes(typeResult.payload);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const filteredDocuments = useMemo(() => {
    if (!searchTerm) return documents;
    const term = searchTerm.toLowerCase();
    return documents.filter((d) => d.originalFileName.toLowerCase().includes(term));
  }, [documents, searchTerm]);

  const mappedCount = documents.filter(
    (d) => mappings[d.id]?.userId && mappings[d.id]?.companyAttachmentTypeId
  ).length;

  const handleMappingChange = (
    docId: number,
    field: 'userId' | 'companyAttachmentTypeId' | 'expiryDate' | 'reminderDays',
    value: string | number
  ) => {
    const current = mappings[docId] || { userId: '', companyAttachmentTypeId: 0 };
    onMappingsChange({
      ...mappings,
      [docId]: { ...current, [field]: value },
    });
  };

  const getFileIcon = (contentType: string) => {
    if (contentType === 'application/pdf') {
      return <FileText className="w-4 h-4 text-red-500" />;
    }
    return <Image className="w-4 h-4 text-blue-500" />;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <div className="px-6 py-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold text-foreground">{t('Map Documents')}</h3>
          <p className="text-sm text-muted-foreground">
            {t('Assign an employee and document type to each uploaded file')}
          </p>
        </div>
        <div className="flex items-center gap-2 text-sm">
          <span
            className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${
              mappedCount === documents.length
                ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                : 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'
            }`}
          >
            {mappedCount === documents.length ? (
              <Check className="w-3 h-3" />
            ) : (
              <AlertCircle className="w-3 h-3" />
            )}
            {mappedCount}/{documents.length} {t('mapped')}
          </span>
        </div>
      </div>

      {/* Search */}
      <div className="relative mb-4">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <input
          type="text"
          placeholder={t('Search files...')}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full pl-9 pr-3 py-2 text-sm rounded-lg border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
        />
      </div>

      {/* Document mapping table */}
      <div className="border border-border rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-muted/50 border-b border-border">
                <th className="text-left px-4 py-3 font-medium text-muted-foreground w-[40%]">
                  {t('File')}
                </th>
                <th className="text-left px-4 py-3 font-medium text-muted-foreground w-[30%]">
                  <div className="flex items-center gap-1.5">
                    <User className="w-3.5 h-3.5" />
                    {t('Employee')}
                  </div>
                </th>
                <th className="text-left px-4 py-3 font-medium text-muted-foreground w-[30%]">
                  <div className="flex items-center gap-1.5">
                    <Tag className="w-3.5 h-3.5" />
                    {t('Document Type')}
                  </div>
                </th>
                <th className="px-3 py-2 text-left rtl:text-right text-xs font-medium text-muted-foreground">
                  {t('documents.expiryDate')}
                </th>
                <th className="px-3 py-2 text-left rtl:text-right text-xs font-medium text-muted-foreground">
                  {t('documents.reminderDays')}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {filteredDocuments.map((doc) => {
                const mapping = mappings[doc.id];
                const isMapped = mapping?.userId && mapping?.companyAttachmentTypeId;

                return (
                  <tr
                    key={doc.id}
                    className={`transition-colors ${
                      isMapped ? 'bg-green-50/50 dark:bg-green-900/5' : 'hover:bg-muted/30'
                    }`}
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2.5">
                        {getFileIcon(doc.contentType)}
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-foreground truncate max-w-[250px]">
                            {doc.originalFileName}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {(doc.fileSize / 1024).toFixed(1)} KB
                          </p>
                        </div>
                        {isMapped && (
                          <Check className="w-4 h-4 text-green-500 shrink-0" />
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <select
                        value={mapping?.userId || ''}
                        onChange={(e) => handleMappingChange(doc.id, 'userId', e.target.value)}
                        className="w-full px-2.5 py-1.5 text-sm rounded-md border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                      >
                        <option value="">{t('Select employee...')}</option>
                        {employees.map((emp) => (
                          <option key={emp.id} value={emp.id}>
                            {emp.fullName}
                            {emp.department ? ` (${emp.department})` : ''}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="px-4 py-3">
                      <select
                        value={mapping?.companyAttachmentTypeId || ''}
                        onChange={(e) =>
                          handleMappingChange(
                            doc.id,
                            'companyAttachmentTypeId',
                            Number(e.target.value)
                          )
                        }
                        className="w-full px-2.5 py-1.5 text-sm rounded-md border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                      >
                        <option value="">{t('Select type...')}</option>
                        {attachmentTypes.map((type) => (
                          <option key={type.id} value={type.id}>
                            {type.attachmentType}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="px-3 py-2">
                      <input
                        type="date"
                        className="border border-input bg-background rounded-md px-2 py-1 text-sm w-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary transition-colors"
                        value={mapping?.expiryDate ?? ''}
                        onChange={(e) => handleMappingChange(doc.id, 'expiryDate', e.target.value)}
                      />
                    </td>
                    <td className="px-3 py-2">
                      <input
                        type="number"
                        min="1"
                        className="border border-input bg-background rounded-md px-2 py-1 text-sm w-20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary transition-colors"
                        value={mapping?.reminderDays ?? ''}
                        placeholder="30"
                        onChange={(e) => handleMappingChange(doc.id, 'reminderDays', e.target.value ? parseInt(e.target.value, 10) : '')}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {employees.length === 0 && (
        <div className="mt-4 flex items-start gap-3 p-3 rounded-lg bg-amber-50 border border-amber-200 dark:bg-amber-900/10 dark:border-amber-800">
          <AlertCircle className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
          <p className="text-sm text-amber-700 dark:text-amber-400">
            {t('No employees found. Please ensure employees are added to your company.')}
          </p>
        </div>
      )}

      {attachmentTypes.length === 0 && (
        <div className="mt-4 flex items-start gap-3 p-3 rounded-lg bg-amber-50 border border-amber-200 dark:bg-amber-900/10 dark:border-amber-800">
          <AlertCircle className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
          <p className="text-sm text-amber-700 dark:text-amber-400">
            {t('No document types configured. Please add attachment types in company settings.')}
          </p>
        </div>
      )}
    </div>
  );
}
